<script setup lang="ts">
/**
 * PATCH /organizations/{id}/members/{memberId}. OrganizationRole: owner · admin · member. Only
 * an owner may grant or take ownership, and the server refuses to demote the last owner; the
 * dialog offers what the caller may do and lets the server's message stand for the rest.
 */
import type { components } from '@scalty/contracts'
import Dialog from '~/components/ui/Dialog.vue'
import Choice from '~/components/ui/Choice.vue'
import Icon from '~/components/ui/Icon.vue'
import ActionButton from '~/components/ui/ActionButton.vue'

type Role = components['schemas']['OrganizationRole']
const props = defineProps<{ member: { name: string; email: string; role: Role }; canGrantOwner: boolean }>()
const emit = defineEmits<{ close: []; submit: [payload: { role: Role }] }>()
const role = ref<Role>(props.member.role)
const roles = computed(() => [
  ...(props.canGrantOwner || props.member.role === 'owner' ? [{ id: 'owner' as const, title: 'Owner', facts: 'Everything, including billing, members and ownership' }] : []),
  { id: 'admin' as const, title: 'Admin', facts: 'Everything except transferring ownership' },
  { id: 'member' as const, title: 'Member', facts: 'Works in projects · cannot change billing or keys' },
])
</script>

<template>
  <Dialog title="Change role" :meta="member.email" :width="480" @close="emit('close')">
    <template #icon><Icon name="shield" /></template>
    <div class="flex flex-col gap-1.5">
      <Choice v-for="entry in roles" :key="entry.id" :title="entry.title" :facts="entry.facts" :selected="role === entry.id" @click="role = entry.id" />
    </div>
    <template #actions>
      <ActionButton label="Cancel" @click="emit('close')" />
      <ActionButton label="Save role" variant="primary" :disabled="role === member.role" @click="emit('submit', { role })"><template #icon><Icon name="check" /></template></ActionButton>
    </template>
  </Dialog>
</template>
